import React, { useMemo, useState } from "react";
import { Alert, Box } from "@mui/material";
import {
  Bar, CartesianGrid, ComposedChart, LabelList, Line, ReferenceLine,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";

import { getDailyTrend } from "../../services/ops/opsservices";
import { useClientTimeZone, zoneAbbr } from "../../utils/clienttimezone";
import {
  BLUE, GREEN, AMBER, RED, NAVY, AVG_INK,
  CounterCard, ChartCard, SectionTitle, Caption, DetailTable, LoadingRow,
  OpsSlicers, StatusChip, useOpsData,
  DEFAULT_GRAN, DEFAULT_RANGE, getDateRange,
  formatRangeLabel, isSubDayGran,
  fmtInt, fmtPct, fmtDuration, fmtDate, fmtPeriod, fmtAxisDate, safeNum,
} from "./opsShared";

const TARGET_UNDER_60 = 95;

export default function DailyTrendDashboard() {
  const [gran,  setGran]  = useState(DEFAULT_GRAN);
  const [range, setRange] = useState(DEFAULT_RANGE);

  const { timeZone, tzReady } = useClientTimeZone();
  const tzLabel = zoneAbbr(timeZone);

  const params = useMemo(() => ({
    ...getDateRange(range, timeZone),
    granularity: gran.toLowerCase(),
  }), [range, gran, timeZone]);

  const { data, loading, error } = useOpsData({ rows: getDailyTrend }, params, tzReady);

  // The API returns newest first for the table; the charts need oldest first.
  const rows = data.rows || [];
  const chart = [...rows].reverse().map((r) => ({
    date:       fmtAxisDate(r.summary_date, gran, timeZone),
    handled:    safeNum(r.conversationsHandled),
    pct:        Math.round(safeNum(r.answeredUnderOneMinutePct)),
    avgSecs:    Math.round(safeNum(r.averageSeconds)),
    outages:    safeNum(r.serviceInterruptions),
    completed:  safeNum(r.paymentsCompleted),
    scheduled:  safeNum(r.paymentsScheduled),
    attempts:   safeNum(r.deliveryAttemptsPerMessage),
  }));

  // Period totals across whatever range is selected — the tiles answer
  // "how did the whole window go", the table answers "which day was bad".
  const totals = rows.reduce((a, r) => ({
    handled:   a.handled   + safeNum(r.conversationsHandled),
    under60:   a.under60   + safeNum(r.answeredUnderOneMinute),
    outages:   a.outages   + safeNum(r.serviceInterruptions),
    completed: a.completed + safeNum(r.paymentsCompleted),
    scheduled: a.scheduled + safeNum(r.paymentsScheduled),
    repeats:   a.repeats   + safeNum(r.messagesSentTwiceOrMore),
  }), { handled: 0, under60: 0, outages: 0, completed: 0, scheduled: 0, repeats: 0 });

  const pctUnder = totals.handled > 0 ? (totals.under60 / totals.handled) * 100 : 0;
  const pctStatus = pctUnder >= TARGET_UNDER_60 ? "ok" : pctUnder >= 80 ? "warn" : "bad";
  const worstAttempts = rows.reduce((a, r) => Math.max(a, safeNum(r.deliveryAttemptsPerMessage)), 0);

  const rangeLabel = formatRangeLabel(params, timeZone);
  const titleOf = (t) => `${t} · ${rangeLabel}`;

  // No account columns: every row here is a whole period, not a customer.
  const columns = [
    { key: "summary_date", label: "Period", width: 130,
      // At Min/Hour the value is a timestamp — a date label would print the
      // same string on every row.
      render: (r) => fmtPeriod(r.summary_date, gran, timeZone) },
    { key: "conversationsHandled", label: "Conversations Handled", width: 150, align: "right",
      render: (r) => fmtInt(r.conversationsHandled) },
    { key: "answeredUnderOneMinutePct", label: "Answered Under 1 Min", width: 150, align: "right",
      render: (r) => {
        const v = safeNum(r.answeredUnderOneMinutePct);
        const tone = v >= TARGET_UNDER_60 ? "ok" : v >= 80 ? "warn" : "bad";
        return <StatusChip label={fmtPct(v)} tone={tone} />;
      } },
    { key: "averageSeconds", label: "Average Response", width: 130, align: "right",
      render: (r) => fmtDuration(r.averageSeconds) },
    { key: "serviceInterruptions", label: "Service Interruptions", width: 145, align: "right",
      render: (r) => {
        const v = safeNum(r.serviceInterruptions);
        return v > 0 ? <StatusChip label={String(v)} tone="bad" /> : "0";
      } },
    { key: "paymentsCompleted", label: "Payments Completed", width: 145, align: "right",
      render: (r) => fmtInt(r.paymentsCompleted) },
    { key: "paymentsScheduled", label: "Payments Scheduled", width: 145, align: "right",
      render: (r) => fmtInt(r.paymentsScheduled) },
    { key: "deliveryAttemptsPerMessage", label: "Delivery Attempts per Message", width: 190, align: "right",
      render: (r) => {
        const v = safeNum(r.deliveryAttemptsPerMessage);
        return v > 1.0001
          ? <StatusChip label={v.toFixed(3)} tone="bad" />
          : <StatusChip label={v.toFixed(3)} tone="ok" />;
      } },
  ];

  return (
    <Box sx={{ p: 2 }}>
      <OpsSlicers gran={gran} setGran={setGran} range={range} setRange={setRange} tzLabel={tzLabel} />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && <LoadingRow text="Building the trend…" />}

      {!loading && (
        <>
          <SectionTitle>Period Totals</SectionTitle>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(6, minmax(0,1fr))", gap: 1, mb: 1.5 }}>
            <CounterCard label="Conversations Handled" value={fmtInt(totals.handled)} color={NAVY} />
            <CounterCard label="Answered Under 1 Minute" value={fmtPct(pctUnder)}
              color={GREEN} status={pctStatus} sub={`Target ${TARGET_UNDER_60}%`} />
            <CounterCard label="Service Interruptions" value={fmtInt(totals.outages)}
              color={GREEN} status={totals.outages === 0 ? "ok" : "bad"} />
            <CounterCard label="Payments Completed" value={fmtInt(totals.completed)} color={GREEN} />
            <CounterCard label="Payments Scheduled" value={fmtInt(totals.scheduled)} color={BLUE} />
            <CounterCard label="Delivery Attempts per Message"
              value={worstAttempts ? worstAttempts.toFixed(3) : "—"}
              color={GREEN} status={worstAttempts > 1.0001 ? "bad" : "ok"}
              sub="Worst in range" />
          </Box>

          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5, mb: 1.5 }}>
            <ChartCard title={titleOf("Volume and reliability")}
              right={`target ${TARGET_UNDER_60}%`} height={280}>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={chart} margin={{ top: 18, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#888" }} interval="preserveStartEnd" minTickGap={24} />
                  <YAxis yAxisId="left" tick={{ fontSize: 10, fill: "#888" }} />
                  <YAxis yAxisId="right" orientation="right" domain={[0, 100]}
                    tick={{ fontSize: 10, fill: GREEN }} tickFormatter={(v) => `${v}%`} />
                  <Tooltip contentStyle={{ fontSize: 12 }} />
                  <ReferenceLine yAxisId="right" y={TARGET_UNDER_60} stroke={AVG_INK}
                    strokeDasharray="3 3" strokeWidth={1} />
                  <Bar yAxisId="left" dataKey="handled" name="Conversations"
                    fill={`${NAVY}33`} radius={[3, 3, 0, 0]} />
                  <Line yAxisId="right" type="monotone" dataKey="pct" name="Answered under 1 min"
                    stroke={GREEN} strokeWidth={2} dot={{ r: 3 }} />
                </ComposedChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title={titleOf("Payments and interruptions")} height={280}>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={chart} margin={{ top: 18, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#888" }} interval="preserveStartEnd" minTickGap={24} />
                  <YAxis tick={{ fontSize: 10, fill: "#888" }} allowDecimals={false} />
                  <Tooltip contentStyle={{ fontSize: 12 }} />
                  <Bar dataKey="completed" name="Completed" stackId="p" fill={GREEN} />
                  <Bar dataKey="scheduled" name="Scheduled" stackId="p" fill={BLUE} radius={[3, 3, 0, 0]} />
                  <Line type="monotone" dataKey="outages" name="Interruptions"
                    stroke={RED} strokeWidth={2} dot={{ r: 3 }}>
                    <LabelList dataKey="outages" position="top" fontSize={10} fill={RED}
                      formatter={(v) => (v > 0 ? v : "")} />
                  </Line>
                </ComposedChart>
              </ResponsiveContainer>
            </ChartCard>
          </Box>
          <Caption>
            Interruptions plotted against payments on one axis on purpose: a day where the
            red line rises and the bars fall is a day the agent stopped collecting, and the
            two numbers are easier to connect side by side than a tab apart.
          </Caption>

          <SectionTitle right={`${rows.length} periods · newest first`}>
            Daily Trend
          </SectionTitle>
          <DetailTable
            columns={columns}
            rows={rows}
            emptyText="No activity in this range."
            maxHeight={480}
          />
          <Caption>
            Delivery Attempts per Message is the integrity check — it must read 1.000 on
            every row. Anything higher means a customer message was sent to AI more than
            once on that day, which is the condition behind duplicate charges.
          </Caption>
        </>
      )}
    </Box>
  );
}